import {
  VoucherStatusSchema,
  type TripFulfillment,
  type TripStatus,
  type TripView,
} from '@voll-intelligence/domain';
import { destinationImageByCity, destinationImageByIata } from '@voll-intelligence/genui-contract';
import * as doc from '@voll-intelligence/trip-document';
import { currentLocale, t } from './i18n.js';
import { documentCtx } from './document_ctx.js';

// A maior parte da derivação de datas/rótulos/serviços morou aqui e se mudou
// para `@voll-intelligence/trip-document` (Tasks 3–7): o painel e a lane de
// PDF leem os MESMOS fatos do mesmo voucher, e duplicar a conta divergiria no
// primeiro ajuste. O que sobra local é o que o pacote não conhece — pill de
// proximidade da home, faixa de dias do calendário, carrossel/sidebar — e os
// wrappers finos que amarram o `t()`/locale do web-chat ao `ctx` que o pacote
// pede.
export {
  calendarDateFromUtcDay,
  cityLabel,
  formatFlightDuration,
  groupStopsByDay,
  canonicalModal,
  canonicalModals,
  hotelCoverage,
  isDeadVoucherStatus,
  isModal,
  isoDateLocal,
  localHHMM,
  parseLocalIso,
  pendingStays,
  splitAirport,
  startOfDay,
  tripAgenda,
  tripCost,
  tripNights,
  tripServices,
  tripStays,
  tripStops,
  voucherStatusPill,
} from '@voll-intelligence/trip-document';

/** O código Voll como chave de busca: sem espaços nas pontas e em maiúsculas. */
export function voucherCodeKey(code: string | null | undefined): string {
  return code?.trim().toUpperCase() ?? '';
}

/** A viagem do viajante que carrega o voucher de código `voucherCode` — o alvo
 *  `voucher` da central de notificações (a reserva é notificada ANTES do trip
 *  sync materializar a viagem, então o servidor só conhece o código). Varre
 *  `trip.vouchers` cru, não `tripServices`: um voucher anexado de modal que o
 *  painel não lista continua sendo DESTA viagem (#4183). `null` quando nenhuma
 *  das viagens recebidas o tem. */
export function findTripByVoucherCode(
  trips: readonly TripView[],
  voucherCode: string,
): TripView | null {
  const key = voucherCodeKey(voucherCode);
  if (!key) return null;
  return (
    trips.find((trip) => trip.vouchers.some((v) => voucherCodeKey(v.voucherCode) === key)) ?? null
  );
}

export type {
  AgendaRow,
  HotelCoverage,
  ModalKind,
  ServiceLeg,
  StayWindow,
  TripCost,
  TripService,
  TripServiceDetail,
  TripStay,
  TripStop,
  TripStopDay,
  VoucherBadgeVariant,
} from '@voll-intelligence/trip-document';

/** Os passos VIVOS do itinerário — a régua da PÁGINA (handoff Trip V2,
 *  2026-08-05): mostra só o que está garantido ou a caminho (em andamento,
 *  aguardando aprovação, emitido); os negativos (recusado/cancelado/expirado)
 *  saem daqui e moram na seção "Precisa de sua atenção". Extraída (Task 5
 *  review, #2205) porque `TripItinerary` (`TripPanel.tsx`) e a mala do topo
 *  (`tripBagCount`, `ConversationTripPanel.tsx`) já tinham CADA UMA a própria
 *  cópia deste `tripStops(trip).filter(...)` — duas rédeas de "quais passos
 *  contam" que divergiriam no primeiro ajuste de status morto. */
export function liveStops(trip: TripView): doc.TripStop[] {
  return doc.tripStops(trip).filter((stop) => !doc.isDeadVoucherStatus(stop.service.status));
}

/** A PRIMEIRA parada de cada serviço vivo, por `service.key` — a unidade de
 *  "UMA reserva": um voo ida-e-volta é DOIS passos (`tripStops` achata por
 *  perna) mas um serviço só, e o mapa devolve UMA entrada para ele. Serve a
 *  DUAS perguntas com a MESMA dedupe (extraída junto de `liveStops` pela
 *  mesma razão): o preço mora no PRIMEIRO passo do serviço (`TripItinerary`
 *  usa `.key` de cada valor para montar `pricedKeys` — a volta não repete o
 *  valor) e a CONTAGEM de reservas distintas é `.size` (a mala do topo usa
 *  isto — `tripBagCount`, `ConversationTripPanel.tsx`). */
export function firstStopByService(stops: readonly doc.TripStop[]): Map<string, doc.TripStop> {
  const first = new Map<string, doc.TripStop>();
  for (const stop of stops) {
    if (!first.has(stop.service.key)) first.set(stop.service.key, stop);
  }
  return first;
}

/** Mês abreviado no locale ativo ("jun" / "Jun" / "jun"). */
export function monthShort(d: Date): string {
  return doc.monthShort(d, documentCtx().locale);
}
/** Faixa compacta: "17 jun" (dia único), "20–22 jun" (mesmo mês), "29 jun – 2 jul"
 *  (meses diferentes); com `today`, o ano fora do ano corrente (ver o original). */
export function formatTripRange(startAt: Date | null, endAt: Date | null, today?: Date): string {
  return doc.formatTripRange(startAt, endAt, documentCtx().locale, today);
}

function capStripDot(s: string): string {
  return s.replace(/\.$/, '').replace(/^./, (c) => c.toUpperCase());
}
/** Mês por extenso no locale ativo ("Junho" / "June" / "Junio"). */
export function monthLong(d: Date): string {
  return capStripDot(new Intl.DateTimeFormat(currentLocale(), { month: 'long' }).format(d));
}
/** Rótulo de 3 letras em maiúsculas do dia da semana ("SEG") no locale ativo. */
export function weekdayShort(d: Date): string {
  return new Intl.DateTimeFormat(currentLocale(), { weekday: 'short' })
    .format(d)
    .replace(/\.$/, '')
    .slice(0, 3)
    .toUpperCase();
}

const DAY_MS = 86_400_000;

export function daysUntil(target: Date, now: Date): number {
  return Math.round((doc.startOfDay(target).getTime() - doc.startOfDay(now).getTime()) / DAY_MS);
}

/** Uma TARJA da faixa: o pedaço de uma viagem que aparece na janela, com o nome dentro.
 *
 *  É ela — e só ela — que mostra o período: quais dias estão ocupados e POR QUEM. A marca
 *  por célula que existia antes era areia sobre areia, e duas viagens vizinhas na janela
 *  ficavam dois blocos indistinguíveis. A faixa nomeada à la Google Calendar é a leitura
 *  do protótipo (`TarjasDeViagem`, em `design/src/templates/trip/painel_da_trip_v2`). */
export interface StripBand {
  tripId: string;
  name: string;
  /** Coluna 1-based onde a tarja começa dentro da janela. */
  colStart: number;
  /** Quantas colunas ela atravessa. */
  span: number;
  /** A ponta esquerda é o começo REAL da viagem (arredonda) e não um corte da janela. */
  realStart: boolean;
  realEnd: boolean;
  /** A viagem ABERTA no painel. */
  isCurrent: boolean;
}

/** Índice do dia dentro da janela que começa em `from`. `Math.round` e não divisão
 *  inteira: no dia da virada de horário de verão a diferença em ms não é múltipla
 *  exata de 24h, e o piso jogaria o dia para o índice anterior. */
function dayOffset(day: Date, from: Date): number {
  return Math.round((doc.startOfDay(day).getTime() - doc.startOfDay(from).getTime()) / DAY_MS);
}

/** As tarjas das viagens que cruzam a janela `[from, from + count - 1]`, em ordem de
 *  camada: a viagem aberta vem POR ÚLTIMO, para vencer quando duas se sobrepõem (o
 *  negócio promete 1:1 por dia, e a promessa se quebra — viagens se emendam e a base
 *  de demonstração já as sobrepõe). Puro: quem decide a janela é o chamador. */
export function tripStripes(
  from: Date,
  count: number,
  trip: TripView,
  otherTrips: readonly TripView[],
  /** O nome JÁ RESOLVIDO da viagem aberta. Vem de fora, e não de `tripName(trip)` aqui,
   *  porque a tela tem UM nome para esta viagem: o mesmo que está no h1 e que o overlay
   *  otimista do renomear atualiza antes da rede responder. Derivado de novo aqui, a
   *  tarja mostraria o nome velho ao lado do título novo. */
  currentName: string,
): StripBand[] {
  const all = [...otherTrips.map((t) => ({ t, isCurrent: false })), { t: trip, isCurrent: true }];
  return all.flatMap(({ t, isCurrent }) => {
    const start = doc.calendarDateFromUtcDay(t.startAt);
    if (!start) return [];
    const end = doc.calendarDateFromUtcDay(t.endAt) ?? start;
    const first = dayOffset(start, from);
    const last = dayOffset(end, from);
    const visibleFirst = Math.max(first, 0);
    const visibleLast = Math.min(last, count - 1);
    if (visibleFirst > visibleLast) return [];
    return [
      {
        tripId: t.id,
        name: isCurrent ? currentName : tripName(t),
        colStart: visibleFirst + 1,
        span: visibleLast - visibleFirst + 1,
        realStart: visibleFirst === first,
        realEnd: visibleLast === last,
        isCurrent,
      },
    ];
  });
}

/** A PISTA (1-based) de cada tarja, alinhada à ordem de entrada: quem se sobrepõe no
 *  tempo desce para a pista seguinte em vez de ser coberto — a camada escondia a outra
 *  viagem inteira, que era o defeito. A viagem ABERTA tem prioridade na pista 1 (ela é
 *  o assunto da tela); as demais entram na primeira pista livre, por ordem de início.
 *  O negócio promete um dia:uma viagem, então quase toda janela sai com uma pista só —
 *  a segunda existe para quando a promessa quebra. */
export function stripeLanes(bands: readonly StripBand[]): number[] {
  const lanes: { start: number; end: number }[][] = [];
  const result = bands.map(() => 1);
  const order = bands
    .map((band, index) => ({ band, index }))
    .sort(
      (a, b) =>
        Number(b.band.isCurrent) - Number(a.band.isCurrent) || a.band.colStart - b.band.colStart,
    );
  for (const { band, index } of order) {
    const start = band.colStart;
    const end = band.colStart + band.span - 1;
    let lane = lanes.findIndex((taken) => taken.every((iv) => end < iv.start || start > iv.end));
    if (lane === -1) {
      lane = lanes.length;
      lanes.push([]);
    }
    lanes[lane]?.push({ start, end });
    result[index] = lane + 1;
  }
  return result;
}

/** `n` dias depois de `date` (negativo anda para trás), no calendário LOCAL — a conta
 *  passa pelo construtor de `Date` e não por aritmética de ms, que erraria uma hora na
 *  virada do horário de verão. */
export function addDays(date: Date, n: number): Date {
  const d = doc.startOfDay(date);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
}

/** O DOMINGO da semana de `date`. A janela da faixa sempre começa num domingo (o
 *  `inicioDaSemanaDe` do protótipo): sem esse alinhamento os rótulos de semana andam de
 *  coluna a cada navegação, e a faixa deixa de ser uma régua de calendário. */
export function weekStart(date: Date): Date {
  const d = doc.startOfDay(date);
  return addDays(d, -d.getDay());
}

/** Onde a janela do carrossel ABRE: no DOMINGO da semana em que a viagem aberta começa
 *  (`useJanelaDoCarrossel` do protótipo). A faixa é uma régua de SEMANA de calendário, e
 *  por isso a janela nasce alinhada ao domingo — até a #2794 ela abria na VÉSPERA do
 *  início (um dia de respiro à esquerda, decisão de 2026-08-21) e as sete colunas
 *  trocavam de dia da semana a cada navegação, que era o defeito. A ÂNCORA continua
 *  sendo a viagem aberta, e não HOJE: a viagem que não cabe na janela do presente mostra
 *  a semana dela, trocar de viagem re-ancora do mesmo jeito, e voltar para o presente é
 *  gesto do viajante (o botão "Hoje"). Viagem sem data de início cai no domingo desta
 *  semana: sem período, o presente é a única âncora que existe. */
export function carouselWindowStart(trip: Pick<TripView, 'startAt'>, now: Date): Date {
  return weekStart(doc.calendarDateFromUtcDay(trip.startAt) ?? now);
}

/** `ok` = cumprido, `soon` = PENDÊNCIA (falta emitir), `info` = contagem para a partida.
 *  `soon` e `info` eram a mesma variante, e não são a mesma pergunta: um pede atenção, o
 *  outro só informa quantos dias faltam. O protótipo V2 pinta a contagem em info. */
type PillVariant = 'ok' | 'soon' | 'info';
export interface TripPillModel {
  variant: PillVariant;
  icon: 'check' | 'clock';
  labelKey: string;
  days?: number;
}
/** Proximidade de UMA data ("Hoje" / "Amanhã" / "Em N dias"). Null sem data. A
 *  viagem usa isto pelo início; a faixa do próximo voo, pela partida do trecho —
 *  cada faixa conta o tempo do que ela mesma anuncia. */
export function datePill(target: Date | null, now: Date): TripPillModel | null {
  if (!target) return null;
  const days = daysUntil(target, now);
  if (days <= 0) return { variant: 'info', icon: 'clock', labelKey: 'trips.pill.today' };
  if (days === 1) return { variant: 'info', icon: 'clock', labelKey: 'trips.pill.tomorrow' };
  return { variant: 'info', icon: 'clock', labelKey: 'trips.pill.inDays', days };
}

/** Pill da foto do card — só ONGOING/UPCOMING chegam aos cards da home.
 *
 *  Recebe o `startAt` CRU e o converte para o DIA DO CALENDÁRIO aqui dentro: o campo é
 *  data-só à meia-noite UTC, e contado cru em fuso negativo (America/Sao_Paulo) o dia
 *  escorrega para trás — a viagem de 17 dizia "Amanhã" em 15 (#2974). A conversão mora
 *  no derivador, não no chamador, pela razão de `isPastTripEndAt`: um chamador convertia
 *  por fora e outro não, e a mesma pergunta tinha duas respostas. NÃO converta antes de
 *  chamar: `calendarDateFromUtcDay` não é idempotente — sobre uma meia-noite LOCAL em
 *  fuso positivo (Europe/Lisbon, Asia/Tokyo) ela volta um dia. */
export function tripPill(trip: TripView, now: Date): TripPillModel {
  if (trip.status === 'ONGOING')
    return { variant: 'ok', icon: 'check', labelKey: 'trips.pill.ongoing' };
  return (
    datePill(doc.calendarDateFromUtcDay(trip.startAt), now) ?? {
      variant: 'soon',
      icon: 'clock',
      labelKey: 'trips.pill.upcoming',
    }
  );
}

export type TripBadgeVariant = 'upcoming' | 'confirmed' | 'gray' | 'cancelled';
/** Badge de situação da viagem — espelha o genui TRIP_STATUS_UI. */
export function tripBadge(status: TripStatus | null | undefined): {
  variant: TripBadgeVariant;
  labelKey: string;
} {
  switch (status) {
    case 'ONGOING':
      return { variant: 'confirmed', labelKey: 'trips.status.ongoing' };
    case 'COMPLETED':
      return { variant: 'gray', labelKey: 'trips.status.completed' };
    case 'CANCELLED':
      return { variant: 'cancelled', labelKey: 'trips.status.cancelled' };
    case 'UPCOMING':
    case null:
    case undefined:
    default:
      return { variant: 'upcoming', labelKey: 'trips.status.upcoming' };
  }
}

/** A ROTA da viagem ("CNF → GIG"); só o destino ou só a origem quando falta a outra
 *  ponta; null sem nenhuma das duas. */
export function tripRoute(trip: Pick<TripView, 'origin' | 'destination'>): string | null {
  const origin = doc.splitAirport(trip.origin).code;
  const destination = doc.splitAirport(trip.destination).code;
  if (origin && destination) return `${origin} → ${destination}`;
  return destination || origin || null;
}

/** O NOME DA VIAGEM — um só, para toda tela que rotula uma viagem.
 *
 *  Precedência: o nome que o viajante escreveu ganha de tudo, para sempre; sem ele, a
 *  CIDADE do destino, senão a da origem — a mesma regra da capa do PDF
 *  (`tripDisplayName`). A janela de datas NÃO entra: a sidebar a mostra na linha de
 *  baixo, o painel no chip, e as superfícies de uma linha colam via
 *  `tripNameWithRange`. Sem nada para nomear, o fallback da tela. */
export function tripName(trip: Pick<TripView, 'origin' | 'destination' | 'manualName'>): string {
  return doc.tripDisplayName(trip) ?? t('shell.tripFallback');
}

/** "São Paulo · 15–18 set" — o nome com a janela colada, para as superfícies de UMA
 *  linha (card do solicitante, aria das setas). Sem janela, só o nome. Um nome MANUAL
 *  pode já trazer a janela ("Paris · 15–18 set"): aí não se cola de novo. A busca
 *  ignora a caixa porque nomes gravados antes do mês minúsculo (#3990) trazem
 *  "15–18 Set". */
export function tripNameWithRange(
  trip: Pick<TripView, 'origin' | 'destination' | 'manualName' | 'startAt' | 'endAt'>,
): string {
  const name = tripName(trip);
  const dates = formatTripRange(
    doc.calendarDateFromUtcDay(trip.startAt),
    doc.calendarDateFromUtcDay(trip.endAt),
  );
  return dates && !name.toLowerCase().includes(dates.toLowerCase()) ? `${name} · ${dates}` : name;
}

/** O texto EXTRA pelo qual a busca encontra a viagem, além do nome: as pontas (com
 *  cidade, como a fonte manda — "CNF - BELO HORIZONTE") e a janela de datas.
 *
 *  Existe porque o nome deixou de carregar esses fatos. Enquanto ele era gerado
 *  ("Viagem CNF → GRU · 12-18 jul"), procurar "MIA" ou "ago" achava a viagem de
 *  graça; com nome escrito por gente, procurar pelo destino pararia de funcionar. */
export function tripSearchText(
  trip: Pick<TripView, 'origin' | 'destination' | 'startAt' | 'endAt'>,
): string {
  const dates = formatTripRange(
    doc.calendarDateFromUtcDay(trip.startAt),
    doc.calendarDateFromUtcDay(trip.endAt),
  );
  return [trip.origin, trip.destination, dates].filter(Boolean).join(' ');
}

/** A pill de PRONTIDÃO da viagem: a situação da emissão (`doc.tripFulfillment`, o
 *  `fulfillment` que a trip-api carimba) traduzida em tom + rótulo. Não é o ciclo de
 *  vida (isso é `tripBadge`, e são perguntas diferentes): aqui é "o que foi contratado
 *  está garantido?". Null quando não há nada a prometer.
 *
 *  Só "Emitida" é `ok`; os outros três pedem atenção (`warn`). Aguardando aprovação e
 *  cancelamento solicitado usam as MESMAS palavras da pill do voucher, para a viagem e
 *  o card do serviço nunca dizerem duas coisas sobre o mesmo estado (#4328). */
export interface TripReadiness {
  fulfillment: TripFulfillment;
  tone: 'ok' | 'warn';
  labelKey: string;
}

const READINESS = {
  ISSUED: { tone: 'ok', labelKey: 'trips.readiness.issued' },
  PENDING: { tone: 'warn', labelKey: 'trips.readiness.pending' },
  AWAITING_APPROVAL: { tone: 'warn', labelKey: 'trips.voucherStatus.AWAITING_APPROVAL' },
  CANCELLATION_REQUESTED: { tone: 'warn', labelKey: 'trips.voucherStatus.CANCELLATION_REQUESTED' },
} as const satisfies Record<TripFulfillment, Omit<TripReadiness, 'fulfillment'>>;

export function tripReadiness(trip: TripView): TripReadiness | null {
  const fulfillment = doc.tripFulfillment(trip);
  return fulfillment ? { fulfillment, ...READINESS[fulfillment] } : null;
}

export interface ModalCount {
  modal: doc.ModalKind;
  count: number;
}
export function tripModals(trip: TripView): ModalCount[] {
  const counts = new Map<doc.ModalKind, number>();
  for (const v of trip.vouchers) {
    const kind = doc.canonicalModal(v.modal);
    if (kind) counts.set(kind, (counts.get(kind) ?? 0) + 1);
  }
  if (counts.size === 0) {
    for (const kind of doc.canonicalModals(trip.modals)) counts.set(kind, 0);
  }
  return doc.MODAL_ORDER.filter((m) => counts.has(m)).map((m) => ({
    modal: m,
    count: counts.get(m) ?? 0,
  }));
}

function iataCode(v: string | null | undefined): string | undefined {
  const match = /^\s*([A-Z]{3})(?:\s*-.*)?$/i.exec(v ?? '');
  return match?.[1]?.toUpperCase();
}

/** Foto do destino p/ o hero e o peek da sidebar: a do enriquecimento primeiro,
 *  senão o mapa IATA→foto do genui-kit (chegada do voo em destaque, senão o
 *  destino da viagem), senão o catálogo por CIDADE. O último degrau é o que
 *  cobre destino gravado como metro ("SAO") ou como nome de cidade (viagem só
 *  de hotel): `cityLabel` traduz sigla e metro para o nome curado do domain, e
 *  o catálogo por cidade é derivado do mesmo mapa por IATA (#2983).
 *  `undefined` quando não há foto conhecida. */
export function tripHeroPhoto(trip: TripView, arrIata?: string | null): string | undefined {
  const mapped =
    destinationImageByIata(iataCode(arrIata)) ??
    destinationImageByIata(iataCode(trip.destination)) ??
    destinationImageByCity(doc.cityLabel(trip.destination));
  return trip.destinationImage?.url ?? mapped;
}

/** Crédito da foto do hero/peek, no formato que o ⓘ exibe. */
export interface HeroCredit {
  author: string;
  provider: string;
  sourceUrl: string;
  license?: string;
  licenseUrl?: string;
}

const PROVIDER_LABEL = { unsplash: 'Unsplash', wikimedia: 'Wikimedia Commons' } as const;

/** Crédito da foto do hero/peek: só quando a foto é a `destinationImage` da viagem (a que
 *  `tripHeroPhoto` escolhe primeiro). As fotos do mapa fixo do genui-kit não têm crédito
 *  estruturado. Wikimedia (CC BY/BY-SA) traz a licença; o link vai para a página da obra.
 *  Foto do catálogo próprio não pede crédito. */
export function tripHeroCredit(trip: TripView): HeroCredit | undefined {
  const image = trip.destinationImage;
  if (!image?.url || image.source === 'voll') return undefined;
  return {
    author: image.photographer,
    provider: PROVIDER_LABEL[image.source],
    sourceUrl: image.sourceUrl ?? image.photographerUrl,
    ...(image.license ? { license: image.license } : {}),
    ...(image.licenseUrl ? { licenseUrl: image.licenseUrl } : {}),
  };
}

// ── O serviço em FOCO no alto do painel ──────────────────────────────────────
// O topo da lateral responde "o que a viagem me pede agora?", e a resposta muda com o
// dia: antes de embarcar é o voo; depois de pousar é o hotel em que se dorme; depois do
// check-out é a volta; no dia do transfer é o transfer. Era só o próximo VOO (a antiga
// `featuredFlight`, apagada com o card da home), então numa viagem já em curso o alto do
// painel repetia um voo que a pessoa tinha pegado e escondia o serviço que ela precisava
// mostrar no balcão.
//
// Granularidade própria do painel (não faz parte do trip-document): lê `tripStops` do
// pacote, mas a REGRA de qual passo ganha destaque é do web-chat.

/** O fim do PASSO — a chegada da perna, quando o passo é perna; senão o fim do serviço. */
function stopEnd(stop: doc.TripStop): Date | null {
  return stop.leg ? stop.leg.arriveAt : stop.service.endsAt;
}

export interface FeaturedService {
  stop: doc.TripStop;
  /** Já começou e ainda não terminou — a estada em que se dorme hoje, o carro que está
   *  com a pessoa, o voo que está no ar. Muda o rótulo de "A seguir" para "Agora". */
  ongoing: boolean;
}

/**
 * O serviço que o alto do painel anuncia. Três regras, nesta ordem:
 *
 * 1. **O que ainda vai começar HOJE ganha de tudo** — é o compromisso do dia, e é o único
 *    que se perde por atraso.
 * 2. **Senão, o que está EM CURSO** — a pessoa está dentro dele (o hotel da noite, o carro
 *    na mão). Entre dois em curso, o que termina primeiro: é o próximo a cobrar algo dela.
 * 3. **Senão, o próximo dia que tem serviço** — e dentro do dia, quem MOVE antes de quem
 *    HOSPEDA, depois pela hora: a régua do itinerário, que `tripStops` já entrega. Não há
 *    ordenação própria aqui — o destaque e a lista abaixo dele não podem divergir (#3952).
 *
 * Fora do jogo: serviço com status morto (cancelado, recusado, expirado — a viagem do
 * print tinha três) e serviço que a fonte não datou. Null quando não sobra nada: aí o
 * painel não tem o que anunciar e a faixa some, como já fazia.
 */
export function featuredService(trip: TripView, now: Date): FeaturedService | null {
  const nowMs = now.getTime();
  const todayMs = doc.startOfDay(now).getTime();
  const dated = doc
    .tripStops(trip)
    .flatMap((stop) =>
      stop.at && !doc.isDeadVoucherStatus(stop.service.status) && !doc.isChangeRequestedStop(stop)
        ? [{ stop, start: stop.at, end: stopEnd(stop) }]
        : [],
    );

  const first = dated.find((x) => x.start.getTime() > nowMs);
  if (first && doc.startOfDay(first.start).getTime() === todayMs) {
    return { stop: first.stop, ongoing: false };
  }

  // `flatMap` e não `filter`: ele ESTREITA o tipo do fim, e sem isso a ordenação abaixo
  // precisaria de dois `!` — proibidos aqui, e com razão (o fim é opcional de verdade).
  const ongoing = dated
    .flatMap((x) =>
      x.end && x.start.getTime() <= nowMs && x.end.getTime() > nowMs
        ? [{ stop: x.stop, end: x.end }]
        : [],
    )
    .sort((a, b) => a.end.getTime() - b.end.getTime());
  if (ongoing[0]) return { stop: ongoing[0].stop, ongoing: true };

  return first ? { stop: first.stop, ongoing: false } : null;
}

/** Ícone de status de UMA viagem no sidebar, derivado dos vouchers. Precedência:
 *  tudo CONFIRMED → check; algum AWAITING_APPROVAL → pending (aguardando
 *  aprovação); algum IN_PROGRESS → progress (em andamento); senão null.
 *
 *  Só o ÍCONE: o rótulo em texto saiu da linha da sidebar (decisão de
 *  2026-08-31) e o status agora se lê no peek, serviço a serviço. Quem consome
 *  isto hoje é o palette da busca. */
export type SidebarTripIcon = 'check' | 'pending' | 'progress';
export interface SidebarTripStatus {
  icon: SidebarTripIcon;
}
/** A viagem tem algo que deu errado? Serviço RECUSADO, EXPIRADO ou CANCELADO
 *  acende o dot de atenção da linha da sidebar (§5.3 do handoff da V6).
 *
 *  Distinto de `tripSidebarStatus`, que fala do andamento normal (confirmada,
 *  em aprovação, em progresso) — este responde só "precisa de atenção". */
export function tripNeedsAttention(trip: TripView): boolean {
  return trip.vouchers.some((voucher) => doc.isDeadVoucherStatus(voucher.status));
}

export function tripSidebarStatus(trip: TripView): SidebarTripStatus | null {
  const voucherStatus = VoucherStatusSchema.enum;
  const statuses = trip.vouchers.map((v) => v.status);
  if (statuses.length > 0 && statuses.every((s) => s === voucherStatus.CONFIRMED))
    return { icon: 'check' };
  if (statuses.includes(voucherStatus.AWAITING_APPROVAL)) return { icon: 'pending' };
  if (statuses.includes(voucherStatus.IN_PROGRESS)) return { icon: 'progress' };
  return null;
}

/** A viagem tem ≥1 ITEM?
 *
 *  Item é serviço VIVO — reservado, em aprovação, emitido ou de status ainda
 *  desconhecido; morto (cancelado/recusado/expirado) não conta, pela régua de
 *  sempre ("o que morreu não promete nada", `isDeadVoucherStatus`). A viagem
 *  provisória do write-ahead TAMBÉM conta: `provisionalTripId` é a viagem do
 *  recibo da reserva em andamento, e o recibo é a prova do item antes de o
 *  voucher reconciliar — sem isso a viagem sumiria da sidebar durante o booking.
 *
 *  Sem voucher NENHUM, quem responde são os `modals`: `vouchers: []` sozinho
 *  também significa "o customer-api não respondeu" (a mesma leitura do
 *  `isEmptiedTrip`, abaixo), e os modals derivam das ORDERS, que
 *  sobrevivem à queda — uma indisponibilidade de leitura não pode esvaziar a
 *  sidebar inteira. Pelo mesmo motivo, `servicesDegraded` fica: sem prova de
 *  vazia, a viagem não some.
 *
 *  É O predicado das superfícies de LISTAGEM (sidebar, calendários, o carrossel
 *  "Minhas viagens" da Home mobile — #5957): um só, para as telas não divergirem no
 *  primeiro ajuste. Filtro de EXIBIÇÃO, não deleção — a viagem vazia continua
 *  acessível por busca/URL direta (#2188). */
export function tripHasItems(
  trip: Pick<TripView, 'id' | 'vouchers' | 'modals' | 'servicesDegraded'>,
  provisionalTripId: string | null = null,
): boolean {
  if (provisionalTripId !== null && trip.id === provisionalTripId) return true;
  if (trip.servicesDegraded) return true;
  if (trip.vouchers.length === 0) return trip.modals.length > 0;
  return trip.vouchers.some((v) => !doc.isDeadVoucherStatus(v.status));
}

/** Buckets do sidebar: PRÓXIMAS (UPCOMING/ONGOING/null e não-passadas, startAt
 *  asc com null por último) e CONCLUÍDAS (COMPLETED/CANCELLED ou endAt < now,
 *  endAt desc). */
export interface SidebarTripBuckets {
  upcoming: TripView[];
  completed: TripView[];
}
/** Régua do balde "Concluídas", compartilhada com a BFF (gate de arquivar): vive no
 *  `trip-document`. Re-exportada aqui porque os leitores do web-chat a pegam deste
 *  módulo. */
export { isPastTripEndAt } from '@voll-intelligence/trip-document';
export function sidebarTripBuckets(trips: readonly TripView[], now: Date): SidebarTripBuckets {
  const done = (t: TripView) => doc.isTripDone(t, now);
  const upcoming = trips
    .filter((t) => !done(t))
    .sort((a, b) => {
      if (!a.startAt) return b.startAt ? 1 : 0;
      if (!b.startAt) return -1;
      return a.startAt.getTime() - b.startAt.getTime();
    });
  const completed = trips
    .filter(done)
    .sort((a, b) => (b.endAt?.getTime() ?? 0) - (a.endAt?.getTime() ?? 0));
  return { upcoming, completed };
}

/** As viagens que a HOME percorre, da mais próxima para a mais distante: a primeira é
 *  "a próxima viagem" e é ela que a tela abre.
 *
 *  É DE PROPÓSITO a mesma lista da sidebar, e não uma ordem própria. Duas razões: era a
 *  única ordenação por proximidade que este app já tinha, e ela resolve VIAGEM EM CURSO
 *  sem caso especial — uma viagem em curso tem `startAt` no passado, logo é a menor
 *  chave e cai em primeiro lugar naturalmente. É também a semântica que o domain declara
 *  no contrato `TripSortSchema` ('proximity': em curso/futuras primeiro) e que o
 *  protótipo do design escreveu com as mesmas palavras ("Próximas ordena pelo que está
 *  mais perto — é a única ordem que faz sentido pra quem vai viajar").
 *
 *  Viagem passada fica FORA: "voltar" anda para uma viagem futura anterior, não para o
 *  histórico. Quem quer o passado usa a sidebar.
 *
 *  Viagem SEM item (`tripHasItems`) fica DENTRO: o recorte de conteúdo é do consumidor da
 *  Home (o carrossel "Minhas viagens", #5957), não daqui. Esta fila também é o pager e as
 *  setas da Trip mobile: filtrada aqui, a viagem aberta que o viajante acabou de esvaziar
 *  sairia da fila com ele dentro dela. */
export function tripCarousel(trips: readonly TripView[], now: Date): TripView[] {
  return sidebarTripBuckets(trips, now).upcoming;
}

/**
 * Viagem sem NADA — zero vouchers E zero modals. `vouchers: []` sozinho também
 * significa "o customer-api não respondeu" (o read model engole a falha), mas
 * `modals` deriva das ORDERS e sobrevive à queda: exigir os dois vazios é o que
 * distingue "viagem esvaziada" de "viagem que não deu para ler agora". Oferecer
 * arquivar numa viagem cheia seria um 409 garantido no clique.
 */
export function isEmptiedTrip(trip: TripView): boolean {
  return trip.vouchers.length === 0 && trip.modals.length === 0;
}

/** Quanto antes da decolagem a viagem vira capa da abertura (#5196). */
const LAUNCH_COVER_LEAD_MS = 12 * 60 * 60 * 1000;

/** O instante que a janela da capa mede: a decolagem do primeiro voo vivo — o instante da
 *  fonte (`departInstant`), não o relógio de parede do aeroporto, que fora do fuso do aparelho
 *  abriria a janela na hora errada; sem ele, o relógio de parede. Sem voo, o início da viagem
 *  às 00:00 do fuso do aparelho (`calendarDateFromUtcDay`: o `startAt` é data-só à meia-noite
 *  UTC, e lido direto cairia às 21h do dia anterior em São Paulo). */
function launchCoverAnchor(trip: TripView): Date | null {
  const flight = liveStops(trip).find(
    (stop) =>
      stop.service.modal === 'FLIGHT' &&
      !doc.isChangeRequestedStop(stop) &&
      (stop.leg?.departInstant ?? stop.leg?.departAt),
  )?.leg;
  return flight?.departInstant ?? flight?.departAt ?? doc.calendarDateFromUtcDay(trip.startAt);
}

/** A capa da ABERTURA do app (#4505, #5196): a primeira viagem da fila do carrossel que
 *  ainda TEM alguma coisa de pé (a ESVAZIADA pelo move fica na fila, mas herda as datas de
 *  quando tinha conteúdo e disputaria a capa), se estiver em andamento ou a menos de 12 h da
 *  decolagem do primeiro voo (sem voo, do início da viagem). Sem data, Home. Decolagem no
 *  passado entra: a viagem começou e o status ainda não virou `ONGOING`. A terminada já está
 *  fora do carrossel. */
export function launchCoverTrip(carousel: readonly TripView[], now: Date): TripView | null {
  const trip = carousel.find((candidate) => !isEmptiedTrip(candidate));
  if (!trip) return null;
  if (trip.status === 'ONGOING') return trip;
  const anchor = launchCoverAnchor(trip);
  return anchor && now.getTime() >= anchor.getTime() - LAUNCH_COVER_LEAD_MS ? trip : null;
}

/**
 * Os DESTINOS do "mover voucher": as outras viagens do viajante, sem as do
 * write-ahead que ele não viaja (`requestedForOthers`). Essas são cartões-sombra
 * do board /requested — escondidas da sidebar, da home e do carrossel
 * (`useVisibleTrips` no `ChatView`) — e mover um voucher para dentro de uma
 * delas o mandaria para uma viagem que a pessoa não consegue nem abrir dali.
 *
 * Um helper SÓ para as superfícies do mover, porque elas divergiam: o painel da
 * página e o da conversa (desktop) partiam da lista COMPLETA e a gaveta da Home
 * Nova (mobile) da já filtrada. A rota `/trips/:id`, a expulsão e as cercas de
 * posse continuam lendo a lista completa — resolver um deep link para uma viagem
 * escondida daqui é outro assunto.
 *
 * A viagem ARQUIVADA também sai: ela está escondida da sidebar e da home pelo
 * mesmo `useVisibleTrips`, e mover um voucher para dentro dela o esconderia junto.
 */
export function voucherMoveTargets(trips: readonly TripView[], currentTripId: string): TripView[] {
  return trips.filter(
    (trip) => trip.id !== currentTripId && !trip.requestedForOthers && !trip.archived,
  );
}
